/**
 * Term Finance rate/ratio helpers. Term is fixed-rate, so most rates arrive
 * pre-computed from the subgraph; these cover the on-chain-derived cases.
 */

const WAD = 1e18
const SECONDS_PER_YEAR = 360 * 86400 // Term uses a 360-day count convention

/**
 * Convert a WAD-scaled maintenance collateral ratio into a loan-to-value
 * fraction. Term over-collateralizes: `maintenanceRatio` is collateral/loan
 * (e.g. 1.25e18 = 125%), so LTV = 1 / (ratio/1e18). Returns 0 for bad input.
 */
export function termMaintenanceRatioToLtv(
  maintenanceRatio: string | undefined,
): number {
  if (!maintenanceRatio) return 0
  let r: bigint
  try {
    r = BigInt(maintenanceRatio)
  } catch {
    return 0
  }
  if (r <= 0n) return 0
  const ratio = Number(r) / WAD
  return ratio > 0 ? 1 / ratio : 0
}

/** Convert a WAD-scaled liquidated-damages factor into a penalty fraction (0.05 = 5%). */
export function termLiquidatedDamagesToPenalty(
  liquidatedDamages: string | undefined,
): number {
  if (!liquidatedDamages) return 0
  try {
    return Number(BigInt(liquidatedDamages)) / WAD
  } catch {
    return 0
  }
}

/**
 * Convert a per-annum discount rate (percent) into the implied fixed lend APR
 * for a repo maturing in `ttmSecs`. For a zero-coupon repo token bought at a
 * discount, the realized annualized yield ≈ the discount rate itself, so this
 * is mostly a pass-through with a floor at 0; kept as a seam for a more precise
 * present-value calc if needed.
 */
export function termDiscountRateToAprPct(
  discountRatePct: number,
  _ttmSecs: number,
): number {
  return discountRatePct > 0 ? discountRatePct : 0
}

/**
 * Convert a Terminal 1 order `offerRate` (1e18-scaled fraction, annualized on
 * Term's 360-day year) into the display APR percent. Deliberately the SAME
 * treatment as auction clearing prices (`rate / WAD * 100`, no 365/360
 * adjustment) so fill-now and auction rates on one row stay comparable —
 * both carry Term's own day-count convention.
 */
export function termOfferRateToAprPct(offerRate: string | undefined): number {
  if (!offerRate) return 0
  let r: bigint
  try {
    r = BigInt(offerRate)
  } catch {
    return 0
  }
  if (r <= 0n) return 0
  return (Number(r) / WAD) * 100
}

export { WAD as TERM_WAD, SECONDS_PER_YEAR as TERM_SECONDS_PER_YEAR }
